import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import AdminModal from '../../components/Admin/AdminModal'
import { AdminStatCardFlex } from './adminUi'
import { IconLabel } from '../../components/icons'

const SUSPEND_REASONS = [
  'Violated Terms of Service',
  'Suspicious Activity',
  'Fake / Misleading Profile',
  'Unverified Identity',
  'Compliance Request',
  'User Request',
  'Other',
]

const PAGE_SIZE = 7

export default function AdminUserList() {
  const [stats, setStats] = useState(null)
  const [users, setUsers] = useState([])
  const [roleFilter, setRoleFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [verifiedFilter, setVerifiedFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [detailUser, setDetailUser] = useState(null)
  const [suspendTarget, setSuspendTarget] = useState(null)
  const [suspendReason, setSuspendReason] = useState('')
  const [suspendNote, setSuspendNote] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    axios.get('/api/admin/users', {
      params: {
        role: roleFilter,
        status: statusFilter,
        verified: verifiedFilter,
        search: search || undefined,
      },
    })
      .then((r) => {
        setStats(r.data.stats)
        setUsers(r.data.users || [])
      })
      .catch(() => toast.error('Failed to load users'))
  }, [roleFilter, statusFilter, verifiedFilter, search])

  useEffect(() => { load() }, [load])

  const filtered = users
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => { setPage(1) }, [roleFilter, statusFilter, verifiedFilter, search])

  const resetFilters = () => {
    setRoleFilter('all')
    setStatusFilter('all')
    setVerifiedFilter('all')
    setSearch('')
  }

  const suspend = async () => {
    if (!suspendTarget || !suspendReason) {
      toast.error('Please select a suspension reason')
      return
    }
    setBusy(true)
    try {
      await axios.post(`/api/admin/users/${suspendTarget.id}/suspend`, {
        reason: suspendReason,
        note: suspendNote || undefined,
      })
      toast.success(`${suspendTarget.display_name} suspended`)
      setSuspendTarget(null)
      setSuspendReason('')
      setSuspendNote('')
      setDetailUser(null)
      load()
    } catch {
      toast.error('Failed to suspend user')
    } finally {
      setBusy(false)
    }
  }

  const unsuspend = async (user) => {
    setBusy(true)
    try {
      await axios.post(`/api/admin/users/${user.id}/unsuspend`)
      toast.success(`${user.display_name} unsuspended`)
      setDetailUser(null)
      load()
    } catch {
      toast.error('Failed to unsuspend user')
    } finally {
      setBusy(false)
    }
  }

  const statusLabel = useMemo(() => ({
    active: 'Active',
    inactive: 'Inactive',
    suspended: 'Suspended',
  }), [])

  return (
    <AdminLayout>
      <div className="admin-stats-row cols-5">
        <AdminStatCardFlex icon="users" cls="blue" label="Total Users" value={stats?.total ?? 0} sub={`+${stats?.newThisMonth ?? 0} this month`} />
        <AdminStatCardFlex icon="building" cls="orange" label="GP (Fund Managers)" value={stats?.gp ?? 0} sub={`${stats?.gpPct ?? 0}% of total`} />
        <AdminStatCardFlex icon="lp" cls="green" label="LP (Investors)" value={stats?.lp ?? 0} sub={`${stats?.lpPct ?? 0}% of total`} />
        <AdminStatCardFlex icon="startup" cls="purple" label="Startups" value={stats?.startup ?? 0} sub={`${stats?.startupPct ?? 0}% of total`} />
        <AdminStatCardFlex icon="link" cls="orange" label="Other" value={stats?.other ?? 0} sub="Advisors / Observer" />
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <span className="admin-table-title">Platform Users</span>
        </div>
        <div className="admin-filter-row">
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="all">All Roles</option>
            <option value="GP">GP</option>
            <option value="LP">LP</option>
            <option value="Startup">Startup</option>
            <option value="ServiceProvider">Advisor</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          <select value={verifiedFilter} onChange={(e) => setVerifiedFilter(e.target.value)}>
            <option value="all">All Verification</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>
          <input
            type="text"
            placeholder="Search by name, email or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="admin-btn sm secondary" onClick={resetFilters}>
            Reset
          </button>
        </div>
        <table>
          <thead>
            <tr>
              <th style={{ width: '26%' }}>User</th>
              <th style={{ width: '22%' }}>Company</th>
              <th style={{ width: '9%' }}>Role</th>
              <th style={{ width: '16%' }}>Last Active</th>
              <th style={{ width: '12%' }}>Status</th>
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((u) => {
              const roleKey = (u.role || '').toLowerCase()
              const la = u.last_active || {}
              return (
                <tr key={u.id}>
                  <td>
                    <div className="admin-user-cell">
                      <span className="name">{u.display_name}</span>
                      <span className="email">{u.work_email}</span>
                      <span className="joined">{u.joined_label}</span>
                    </div>
                  </td>
                  <td>
                    <div className="admin-company-cell">
                      <span className="name">{u.company_name || '— (Individual)'}</span>
                      <span className="type">{u.company_subtitle}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-role-badge ${roleKey === 'serviceprovider' ? 'admin' : roleKey}`}>
                      {u.role === 'ServiceProvider' ? 'Advisor' : u.role}
                    </span>
                  </td>
                  <td>
                    <span className="admin-activity-cell" style={la.danger ? { color: '#dc2626' } : la.warn ? { color: '#ea580c' } : undefined}>
                      {la.text}{la.text && la.strong ? ' ' : ''}
                      {la.strong && <strong>{la.strong}</strong>}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-user-status ${u.display_status}`}>
                      {statusLabel[u.display_status] || u.display_status}
                    </span>
                  </td>
                  <td className="admin-td-actions">
                    <div className="admin-action-btns">
                      <button
                        type="button"
                        className="admin-abtn admin-abtn-view"
                        onClick={() => setDetailUser(u)}
                      >
                        View
                      </button>
                      {u.display_status === 'suspended' ? (
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm admin-btn-success"
                          disabled={busy}
                          onClick={() => unsuspend(u)}
                        >
                          <IconLabel name="checkCircle" size="xs">Unsuspend</IconLabel>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="admin-btn admin-btn-sm admin-btn-danger-ghost"
                          disabled={busy}
                          onClick={() => {
                            setSuspendTarget(u)
                            setSuspendReason('')
                            setSuspendNote('')
                          }}
                        >
                          <IconLabel name="moderation" size="xs">Suspend</IconLabel>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
            {!pageItems.length && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--admin-text-muted)' }}>
                  No users match your filters
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="admin-pagination">
          <div className="admin-pagination-info">
            Showing {filtered.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} users
          </div>
          <div className="admin-pagination-controls">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" className={page === n ? 'active' : ''} onClick={() => setPage(n)}>{n}</button>
            ))}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
          </div>
        </div>
      </div>

      <AdminModal
        open={!!detailUser}
        title={detailUser?.display_name || 'User details'}
        subtitle={detailUser?.work_email}
        wide
        onClose={() => setDetailUser(null)}
        footer={(
          <>
            <button type="button" className="admin-btn secondary" onClick={() => setDetailUser(null)}>Close</button>
            {detailUser?.display_status !== 'suspended' && (
              <button
                type="button"
                className="admin-btn admin-btn-warning-ghost"
                onClick={() => {
                  setSuspendTarget(detailUser)
                  setSuspendReason('')
                  setSuspendNote('')
                }}
              >
                Suspend user
              </button>
            )}
          </>
        )}
      >
        {detailUser && (
          <>
            <div className="admin-detail-grid">
              <div className="admin-detail-item"><div className="label">Company</div><div className="value">{detailUser.company_name || '—'}</div></div>
              <div className="admin-detail-item"><div className="label">Location</div><div className="value">{detailUser.hq_region || '—'}</div></div>
              <div className="admin-detail-item"><div className="label">Joined</div><div className="value">{detailUser.created_at ? new Date(detailUser.created_at).toISOString().slice(0, 10) : '—'}</div></div>
              <div className="admin-detail-item"><div className="label">Last Active</div><div className="value">{detailUser.updated_at ? new Date(detailUser.updated_at).toLocaleString('en-GB') : '—'}</div></div>
              <div className="admin-detail-item"><div className="label">Email Verified</div><div className="value">{detailUser.is_verified ? 'Verified' : 'Unverified'}</div></div>
              <div className="admin-detail-item"><div className="label">Plan</div><div className="value">{(detailUser.company_plan || detailUser.is_paid) ? 'Paid' : 'Free'}</div></div>
              <div className="admin-detail-item"><div className="label">Role</div><div className="value">{detailUser.role === 'ServiceProvider' ? 'Advisor' : detailUser.role}</div></div>
              <div className="admin-detail-item"><div className="label">Account Status</div><div className="value">{statusLabel[detailUser.display_status] || detailUser.display_status}</div></div>
              <div className="admin-detail-item" style={{ gridColumn: '1 / -1' }}><div className="label">User ID</div><div className="value" style={{ fontSize: 11, wordBreak: 'break-all' }}>{detailUser.id}</div></div>
            </div>
          </>
        )}
      </AdminModal>

      <AdminModal
        open={!!suspendTarget}
        title="Suspend user"
        onClose={() => setSuspendTarget(null)}
        footer={(
          <>
            <button type="button" className="admin-btn secondary" onClick={() => setSuspendTarget(null)}>Cancel</button>
            <button
              type="button"
              className="admin-btn"
              style={{ background: 'var(--admin-danger)', color: '#fff' }}
              disabled={busy}
              onClick={suspend}
            >
              Confirm suspension
            </button>
          </>
        )}
      >
        {suspendTarget && (
          <>
            <p style={{ fontSize: 12.5, color: 'var(--admin-text-sec)', marginBottom: 14 }}>
              Suspending <strong>{suspendTarget.display_name}</strong>. They will lose access immediately.
            </p>
            <div className="field">
              <label htmlFor="suspendReason">Suspension Reason *</label>
              <select id="suspendReason" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)}>
                <option value="">Select reason...</option>
                {SUSPEND_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="suspendNote">Internal Note (optional — not shown to user)</label>
              <textarea
                id="suspendNote"
                rows={3}
                value={suspendNote}
                onChange={(e) => setSuspendNote(e.target.value)}
                placeholder="Additional context for the audit log..."
              />
            </div>
          </>
        )}
      </AdminModal>
    </AdminLayout>
  )
}
